import React from "react";
import { motion } from "framer-motion";
import { Scissors, Bath, Leaf, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { IMG_GROOM, IMG_WASH, IMG_NAGAYU } from "@/i18n/translations";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const ServicesOverview = () => {
  const { t } = useLang();
  const s = t.services;

  const cards = [
    { img: IMG_GROOM, icon: Scissors, title: s.grooming_title, desc: s.grooming_desc, cta: s.grooming_cta, target: "grooming", testid: "service-grooming", accent: "brand" },
    { img: IMG_WASH, icon: Bath, title: s.selfwash_title, desc: s.selfwash_desc, cta: s.selfwash_cta, target: "selfwash", testid: "service-selfwash", accent: "bamboo" },
    { img: IMG_NAGAYU, icon: Leaf, title: s.nagayu_title, desc: s.nagayu_desc, cta: s.nagayu_cta, target: "nagayu", testid: "service-nagayu", accent: "brand" },
  ];

  return (
    <section id="services" className="relative py-24 sm:py-32" data-testid="services">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-bamboo">{s.tag}</span>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-ink tracking-tight leading-[1.05]">{s.title}</h2>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {cards.map((c, i) => {
            const Icon = c.icon;
            const isBamboo = c.accent === "bamboo";
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="group relative rounded-[1.75rem] overflow-hidden bg-white border border-stone-alt shadow-[0_10px_40px_rgba(42,37,34,0.06)] hover:shadow-[0_20px_50px_rgba(42,37,34,0.12)] hover:-translate-y-1.5 transition-[transform,box-shadow] duration-400"
                data-testid={c.testid}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/50 to-transparent" />
                  <span className={`absolute top-4 left-4 grid place-items-center h-12 w-12 rounded-2xl backdrop-blur text-brand-cream ${isBamboo ? "bg-bamboo/90" : "bg-brand/90"}`}>
                    <Icon className="h-6 w-6" />
                  </span>
                </div>
                <div className="p-7">
                  <h3 className="font-heading text-2xl font-semibold text-brand-ink">{c.title}</h3>
                  <p className="mt-3 text-brand-muted leading-relaxed text-sm">{c.desc}</p>
                  <button
                    data-testid={`${c.testid}-cta`}
                    onClick={() => scrollTo(c.target)}
                    className={`mt-5 inline-flex items-center gap-1.5 font-bold no-a11y-underline group/btn ${isBamboo ? "text-bamboo-deep" : "text-brand"}`}
                  >
                    {c.cta}
                    <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform duration-200" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
